import { useEffect, useState } from "react";
import { api, apiErr, qrImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Loader2, User, Award, QrCode, Printer, Camera, Upload, FileSignature, ShieldCheck, Images } from "lucide-react";
import PhotoCropper from "@/components/PhotoCropper";
import { toast } from "sonner";

const DROPOFF_PREF = { collected_stop: "🚏 Collect from nearest bus stop", walk_home: "🚶 May walk home from bus stop", door: "🏠 Bus can reach the door" };

const MEDIA = [
  ["photo", "PHOTO ONLY — I approve photographs of my child."],
  ["video", "VIDEO ONLY — I approve video recordings of my child."],
  ["both", "BOTH — I approve photographs AND video recordings."],
  ["neither", "NEITHER — Do not photograph or video my child."],
];
const MEDIA_LABEL = { photo: "Photo only", video: "Video only", both: "Photo & video", neither: "No media" };

const CONSENTS = [
  ["terms", "I accept the Adventure Rock program terms"],
  ["emergency", "I authorize emergency medical treatment if needed"],
  ["photo", "I permit photo/video of my child for program use"],
  ["pickup", "I understand the authorized pickup policy"],
  ["snacks", "I permit snack purchasing via the Adventure Wallet"],
  ["inflatable", "I accept the inflatable park safety rules & waiver"],
  ["offsite", "I permit off-site activity participation when applicable"],
];

const FIELDS = [
  ["first_name", "First name", true], ["last_name", "Last name", true], ["preferred_name", "Preferred name"],
  ["date_of_birth", "Date of birth", false, "date"], ["school", "School"], ["grade", "Grade / Year"],
  ["guardian_name", "Parent/guardian name"], ["parent_phone", "Parent phone"], ["secondary_guardian", "Secondary guardian"],
  ["emergency_contact", "Emergency contact"], ["emergency_phone", "Emergency contact phone"], ["authorized_pickups", "Authorized pickup persons"],
  ["allergy_severity", "Allergy severity (mild / severe)"], ["allergy_emergency_contact", "Allergy emergency contact"], ["allergy_emergency_phone", "Allergy emergency phone"],
];
const TEXTAREAS = [
  ["allergies", "Allergies"], ["dietary", "Dietary restrictions"], ["medical", "Relevant medical information"],
  ["medications", "Medications / instructions"], ["special_support", "Special support requirements"], ["interests", "Preferred activities / interests"],
  ["foods_to_avoid", "Foods to avoid (for Dinner Club safety)"], ["allergy_instructions", "Allergy instructions for staff"],
  ["food_preferences", "Food preferences — how they like their meals"],
];
const FUN = [
  ["favorite_color", "Favourite colour"], ["favorite_animal", "Favourite animal"],
  ["favorite_food", "Favourite snack / food"], ["favorite_activity", "Favourite thing to do"],
  ["when_i_grow_up", "When I grow up, I want to be…"], ["superpower", "If I had a superpower, it would be…"],
];

function empty() { return { consents: {} }; }

export default function Explorers() {
  const [kids, setKids] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [mine, setMine] = useState({});
  const [idCard, setIdCard] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [waiverKid, setWaiverKid] = useState(null);
  const [galleryKid, setGalleryKid] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [wForm, setWForm] = useState({ participation_ack: false, emergency_ack: false, media_consent: "", signature: "" });
  const [wBusy, setWBusy] = useState(false);

  const load = () => {
    api.get("/children").then((r) => setKids(r.data)).catch(() => {});
    api.get("/badges/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/badges").then((r) => setBadgeDefs(r.data)).catch(() => {}); }, []);

  const badgeName = (key) => badgeDefs.find((b) => b.key === key)?.name || key;

  const ageOf = (dob) => { if (!dob) return null; const d = new Date(dob); if (isNaN(d)) return null; const t = new Date(); let a = t.getFullYear() - d.getFullYear(); const m = t.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--; return a >= 0 && a < 25 ? a : null; };
  const pickPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file || !editId) return;
    setCropSrc(URL.createObjectURL(file));
    e.target.value = "";
  };
  const uploadCropped = async (blob) => {
    const fd = new FormData(); fd.append("file", blob, "id-photo.jpg");
    try { const { data } = await api.post(`/children/${editId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, photo_url: data.photo_url })); toast.success("Photo uploaded!"); setCropSrc(null); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startAdd = () => { setForm(empty()); setEditId(null); setOpen(true); };
  const startEdit = (k) => { setForm({ ...k }); setEditId(k.id); setOpen(true); };

  const openGallery = async (k) => {
    setGalleryKid(k); setGalleryPhotos([]);
    try { const { data } = await api.get(`/children/${k.id}/activity-photos`); setGalleryPhotos(data); } catch { /* empty */ }
  };

  const openWaiver = (k) => {
    const w = k.waiver || {};
    setWForm({ participation_ack: !!w.signed, emergency_ack: !!w.signed, media_consent: w.media_consent || "", signature: w.signature || "" });
    setWaiverKid(k);
  };
  const signWaiver = async () => {
    if (!wForm.participation_ack || !wForm.emergency_ack) return toast.error("Please tick both acknowledgements.");
    if (!wForm.media_consent) return toast.error("Please choose a photo / video option.");
    if ((wForm.signature || "").trim().length < 2) return toast.error("Please type your full name to sign.");
    setWBusy(true);
    try { await api.post(`/children/${waiverKid.id}/waiver`, wForm); toast.success("Waiver signed — thank you!"); setWaiverKid(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setWBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/children/${editId}`, form);
      else await api.post("/children", form);
      toast.success("Explorer saved!");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display font-extrabold text-2xl text-navy">My Explorers</h1><p className="text-slate-500">Add and manage your children's profiles.</p></div>
        <Button onClick={startAdd} className="bg-turquoise text-navy font-bold rounded-full self-start sm:self-auto shrink-0" data-testid="add-explorer-btn"><Plus className="w-4 h-4 mr-1" /> Add Explorer</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {kids.map((k) => (
          <div key={k.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between" data-testid={`explorer-card-${k.id}`}>
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center overflow-hidden">{k.photo_url ? <img src={k.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-navy" />}</div>
              <div>
                <p className="font-display font-bold text-navy">{k.preferred_name || k.first_name} {k.last_name}</p>
                <p className="text-xs font-mono text-turquoise">{k.explorer_id}</p>
                <p className="text-sm text-slate-500 mt-1">{k.school || "—"} · {k.grade || "—"}</p>
                {k.allergies && <p className="text-xs text-coral mt-1">⚠ {k.allergies}</p>}
                <p className={`text-xs mt-1 font-semibold flex items-center gap-1 ${k.waiver?.signed ? "text-emerald-600" : "text-coral"}`} data-testid={`waiver-status-${k.id}`}>
                  {k.waiver?.signed ? <><ShieldCheck className="w-3 h-3" /> Waiver signed</> : <><FileSignature className="w-3 h-3" /> Waiver required</>}
                </p>
                {(mine[k.id] || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid={`explorer-badges-${k.id}`}>
                    {(mine[k.id] || []).map((bk) => (
                      <span key={bk} className="inline-flex items-center gap-1 bg-gold/15 text-amber-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                        <Award className="w-3 h-3" /> {badgeName(bk)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button variant="ghost" size="icon" onClick={() => setIdCard(k)} data-testid={`idcard-btn-${k.id}`}><QrCode className="w-4 h-4 text-turquoise" /></Button>
              <Button variant="ghost" size="icon" onClick={() => openGallery(k)} data-testid={`gallery-btn-${k.id}`}><Images className="w-4 h-4 text-turquoise" /></Button>
              <Button variant="ghost" size="icon" onClick={() => openWaiver(k)} data-testid={`waiver-btn-${k.id}`}><FileSignature className={`w-4 h-4 ${k.waiver?.signed ? "text-emerald-500" : "text-coral"}`} /></Button>
              <Button variant="ghost" size="icon" onClick={() => startEdit(k)} data-testid={`edit-explorer-${k.id}`}><Pencil className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {kids.length === 0 && <p className="text-slate-400 col-span-2">No explorers yet. Add your first one to begin booking.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="explorer-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{editId ? "Edit Explorer" : "New Explorer"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Enter your explorer's profile, medical details and consents.</DialogDescription>
          <form onSubmit={save} className="space-y-4">
            {editId ? (
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                <div className="w-16 h-16 rounded-full bg-white border overflow-hidden flex items-center justify-center">{form.photo_url ? <img src={form.photo_url} alt="" className="w-full h-full object-cover" /> : <Camera className="w-6 h-6 text-slate-300" />}</div>
                <label className="text-sm font-semibold text-turquoise cursor-pointer" data-testid="photo-upload-label"><Upload className="w-4 h-4 inline mr-1" /> Upload ID photo<input type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="photo-upload-input" /></label>
              </div>
            ) : (
              <p className="text-xs text-slate-400">Save this explorer, then re-open to upload their ID photo.</p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, l, req, type]) => (
                <div key={k}><Label className="text-xs">{l}{req ? " *" : ""}</Label><Input type={type || "text"} value={form[k] || ""} onChange={set(k)} required={req} data-testid={`child-${k}`} className="rounded-xl" /></div>
              ))}
            </div>
            {TEXTAREAS.map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Textarea value={form[k] || ""} onChange={set(k)} data-testid={`child-${k}`} className="rounded-xl" rows={2} /></div>
            ))}
            <div className="bg-turquoise/5 border border-turquoise/20 rounded-xl p-4 space-y-3">
              <p className="font-semibold text-navy text-sm">Get to know this Explorer <span className="font-normal text-slate-400">— fun facts printed on their ID card</span></p>
              <div className="grid sm:grid-cols-2 gap-3">
                {FUN.map(([k, l]) => (
                  <div key={k}><Label className="text-xs">{l}</Label><Input value={form[k] || ""} onChange={set(k)} data-testid={`child-${k}`} className="rounded-xl" /></div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="font-semibold text-navy text-sm">Consents</p>
              {CONSENTS.map(([k, l]) => (
                <label key={k} className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                  <Checkbox checked={!!form.consents?.[k]} onCheckedChange={(v) => setForm({ ...form, consents: { ...form.consents, [k]: !!v } })} data-testid={`consent-${k}`} />
                  {l}
                </label>
              ))}
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-navy rounded-full font-bold" data-testid="save-explorer-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Explorer"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!idCard} onOpenChange={(o) => !o && setIdCard(null)}>
        <DialogContent data-testid="idcard-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Explorer ID Card</DialogTitle></DialogHeader>
          {idCard && (
            <div>
              <div className="rounded-2xl bg-gradient-to-br from-navy to-ocean text-white p-5">
                <div className="flex items-center justify-between">
                  <div><p className="font-display font-black text-lg">ADVENTURE ROCK</p><p className="text-turquoise text-xs tracking-widest">EXPLORER ID</p></div>
                  <img src={qrImage(idCard.id_token || idCard.explorer_id, 80)} className="w-16 h-16 rounded bg-white p-1" alt="QR" />
                </div>
                <div className="flex gap-3 mt-3">
                  <div className="w-20 h-20 rounded-xl bg-white/10 overflow-hidden flex items-center justify-center">{idCard.photo_url ? <img src={idCard.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-white/40" />}</div>
                  <div className="text-sm">
                    <p className="font-display font-bold text-xl">{idCard.preferred_name || idCard.first_name} {idCard.last_name}</p>
                    <p className="font-mono text-gold text-xs">{idCard.explorer_id}</p>
                    <p className="text-white/70">DOB: {idCard.date_of_birth || "—"}{ageOf(idCard.date_of_birth) ? ` · Age ${ageOf(idCard.date_of_birth)}` : ""}</p>
                    <p className="text-white/70">{idCard.school || "—"}</p>
                    {idCard.allergies && <p className="text-coral">⚠ {idCard.allergies}</p>}
                    {idCard.dropoff_preference && <p className="text-turquoise text-xs mt-1">{DROPOFF_PREF[idCard.dropoff_preference] || idCard.dropoff_preference}</p>}
                  </div>
                </div>
                {FUN.some(([k]) => idCard[k]) && (
                  <div className="mt-4 pt-3 border-t border-white/15" data-testid="idcard-funfacts">
                    <p className="text-turquoise text-[10px] tracking-widest mb-1">GET TO KNOW ME</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      {FUN.filter(([k]) => idCard[k]).map(([k, l]) => (
                        <p key={k} className="text-white/85"><span className="text-white/50">{l.replace(/…$/, "").replace(/^Favourite /, "Fav. ")}:</span> {idCard[k]}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={() => window.print()} className="w-full mt-4 bg-navy rounded-full font-bold" data-testid="print-idcard-btn"><Printer className="w-4 h-4 mr-1" /> Print ID Card</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {cropSrc && <PhotoCropper src={cropSrc} onCancel={() => setCropSrc(null)} onCropped={uploadCropped} />}

      <Dialog open={!!waiverKid} onOpenChange={(o) => !o && setWaiverKid(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="waiver-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Parent / Guardian Waiver & Media Consent</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs">For {waiverKid?.preferred_name || waiverKid?.first_name} {waiverKid?.last_name} · After-School Program · The Berkeley Cultural Center, Pembroke</DialogDescription>
          {waiverKid?.waiver?.signed && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="waiver-signed-note">
              <ShieldCheck className="w-4 h-4" /> Signed by {waiverKid.waiver.signed_by_name || "you"} on {new Date(waiverKid.waiver.signed_at).toLocaleDateString()} · {MEDIA_LABEL[waiverKid.waiver.media_consent]}. You can re-sign to update your choices.
            </div>
          )}
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="font-semibold text-navy text-sm">Participation & Assumption of Risk</p>
              <p className="text-xs text-slate-600 leading-relaxed">Adventure Rock Bermuda includes supervised physical and recreational activities — guided play, games, arts and crafts, reading, creative activities and use of indoor inflatable play equipment. Active play and inflatable activities involve ordinary risks such as slips, falls, bumps, collisions and accidental injury. My child may participate unless I have disclosed a restriction on their profile, must follow staff instructions and program safety rules. This form does not waive any rights that cannot lawfully be waived.</p>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={wForm.participation_ack} onCheckedChange={(v) => setWForm({ ...wForm, participation_ack: !!v })} data-testid="waiver-participation" />
                I have read and agree to the participation and safety acknowledgement.
              </label>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="font-semibold text-navy text-sm">Emergency Care Authorization</p>
              <p className="text-xs text-slate-600 leading-relaxed">If I cannot be reached in an emergency, I authorize Adventure Rock staff to provide reasonable first aid and obtain emergency medical assistance for my child when reasonably necessary. Staff will make reasonable efforts to contact me or my emergency contact.</p>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={wForm.emergency_ack} onCheckedChange={(v) => setWForm({ ...wForm, emergency_ack: !!v })} data-testid="waiver-emergency" />
                I authorize emergency first aid / medical assistance if reasonably necessary.
              </label>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="font-semibold text-navy text-sm">Photo & Video Permission <span className="font-normal text-slate-400">— choose one</span></p>
              <p className="text-xs text-slate-600 leading-relaxed">If permission is given, Adventure Rock may use approved media for program updates, website, social media and promotional materials. A child's full name will not be intentionally published with media without separate permission. You may revoke future media permission in writing; previously published material may remain in circulation.</p>
              <div className="grid gap-2">
                {MEDIA.map(([k, l]) => (
                  <button type="button" key={k} onClick={() => setWForm({ ...wForm, media_consent: k })} data-testid={`waiver-media-${k}`}
                    className={`text-left rounded-xl border-2 px-3 py-2 text-sm transition-colors ${wForm.media_consent === k ? "border-turquoise bg-turquoise/10 text-navy font-semibold" : "border-slate-200 text-slate-600 hover:border-turquoise/50"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Signature — type your full name *</Label>
              <Input value={wForm.signature} onChange={(e) => setWForm({ ...wForm, signature: e.target.value })} placeholder="Parent / guardian full name" className="rounded-xl mt-1" data-testid="waiver-signature" />
              <p className="text-[11px] text-slate-400 mt-1">By typing your name you confirm the information is accurate and you are authorized to provide this consent.</p>
            </div>
            <Button onClick={signWaiver} disabled={wBusy} className="w-full bg-navy rounded-full font-bold" data-testid="waiver-submit-btn">{wBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign & Submit Waiver"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!galleryKid} onOpenChange={(o) => !o && setGalleryKid(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="gallery-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy flex items-center gap-2"><Images className="w-5 h-5 text-turquoise" /> {galleryKid?.preferred_name || galleryKid?.first_name}'s Day Gallery</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs">Photos shared by our team during the day.</DialogDescription>
          {galleryPhotos.length === 0 && <p className="text-slate-400 text-sm py-6 text-center" data-testid="gallery-dialog-empty">No photos yet — check back after the next adventure!</p>}
          <div className="grid grid-cols-2 gap-2">
            {galleryPhotos.map((p) => (
              <div key={p.id} className="rounded-xl overflow-hidden bg-slate-100" data-testid={`explorer-photo-${p.id}`}>
                <img src={p.url} alt={p.caption} className="w-full h-36 object-cover" />
                <div className="p-1.5"><p className="text-[10px] text-slate-400">{new Date(p.created_at).toLocaleDateString("en", { month: "short", day: "numeric" })}{p.uploader_name ? ` · ${p.uploader_name}` : ""}</p>{p.caption && <p className="text-[11px] text-slate-600">{p.caption}</p>}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
